/**
 * Pure questions the turn UI asks about a position (docs/ux.md §3). Everything is derived from
 * the rules engine's queries, so the UI never offers what the engine would refuse.
 */
import {
  declarableShips,
  indexMap,
  parseCellId,
  reachableCells,
  fireTargets,
  type Action,
  type GameState,
} from '@brig/rules-engine';

/** Whether a ship stands on a port cell (U-MUST-02 "All except in port"). */
export function inPortCell(state: GameState, shipId: string): boolean {
  const ship = state.ships.find((s) => s.id === shipId);
  const p = ship ? parseCellId(ship.cell) : null;
  return p !== null && indexMap(state.map).portOf(p) !== null;
}

/** Ships that may join the squadron now, in roster order. */
export function squadronCandidates(state: GameState): string[] {
  return declarableShips(state);
}

/**
 * The squadron to propose at the start of a turn (U-MUST-07): the ships of this side that
 * sailed in its previous turn and may still sail.
 */
export function previousSquadron(state: GameState, actions: readonly Action[]): string[] {
  const side = state.turn.side;
  const last = actions.findLast((a) => a.type === 'declareMoveGroup' && a.side === side);
  if (last?.type !== 'declareMoveGroup') return [];
  const ok = new Set(declarableShips(state));
  return last.shipIds.filter((id) => ok.has(id));
}

/** The engine actions of "Cast the wind" (U-MUST-04): declare and spin as one intent. */
export function castActions(state: GameState, squadron: readonly string[]): Action[] {
  const side = state.turn.side;
  const spin: Action = { type: 'spinForMovement', side };
  // A saved game may stop between the two (an older sandbox log): only the spin is left.
  if (state.turn.phase === 'declared') return [spin];
  return [{ type: 'declareMoveGroup', side, shipIds: [...squadron] }, spin];
}

/** Squadron ships that may still make their move (U-MOVE-01 "not moved" badge). */
export function shipsToMove(state: GameState): string[] {
  if (state.turn.phase !== 'spun') return [];
  return state.ships
    .filter((s) => s.side === state.turn.side && s.inGroup && !s.moved && !s.sunk)
    .filter((s) => reachableCells(state, s.id).length > 0)
    .map((s) => s.id);
}

/**
 * Own ships and forts that may still fire this turn: shots left, allowed to fire at all (not in
 * dock, not on the enemy Royal Port…, the engine's `fireTargets`) and a target in reach.
 */
export function unitsToFire(state: GameState): string[] {
  const side = state.turn.side;
  const units = [
    ...state.ships.filter((s) => s.side === side && !s.sunk && s.shotsLeft > 0),
    ...state.forts.filter((f) => f.side === side && !f.destroyed && f.shotsLeft > 0),
  ];
  return units.filter((u) => fireTargets(state, u.id).length > 0).map((u) => u.id);
}

/** What the player would leave unused by ending the turn now (U-END-02). */
export interface TurnSummary {
  readonly notMoved: readonly string[];
  readonly notFired: readonly string[];
}

export function turnSummary(state: GameState): TurnSummary {
  return { notMoved: shipsToMove(state), notFired: unitsToFire(state) };
}

export function isEmptySummary(summary: TurnSummary): boolean {
  return summary.notMoved.length === 0 && summary.notFired.length === 0;
}
